import {
	BadgeCheck,
	Building2,
	Calendar,
	Factory,
	MapPin,
	TriangleAlert,
	Users,
} from "lucide-react";
import { ClientOnly } from "~/components/ui/client-only";
import type { CompanyProfile } from "~/types/company";
import { CopyButton } from "./copy-button";
import { WatchButton } from "./watch-button";

export const COMPANY_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
	ACTIVE: { label: "Aktivní", className: "badge badge-soft badge-success" },
	DISSOLVED: { label: "Zrušená", className: "badge badge-soft badge-error" },
	IN_LIQUIDATION: { label: "V likvidaci", className: "badge badge-soft badge-warning" },
	IN_INSOLVENCY: { label: "V insolvenci", className: "badge badge-soft badge-error" },
	SUSPENDED: { label: "Pozastavená", className: "badge badge-soft badge-warning" },
};

interface Props {
	company: CompanyProfile;
}

export function CompanyHeader({ company }: Props) {
	const status = COMPANY_STATUS_CONFIG[company.status] ?? {
		label: company.status,
		className: "badge badge-soft",
	};

	const address = [company.street, company.city, company.zip].filter(Boolean).join(", ");
	const foundingYear = company.foundingDate ? new Date(company.foundingDate).getFullYear() : null;
	const vat = company.vatRegistration;
	const dic = vat?.dic ?? company.dic;

	const pills: { icon: React.ReactNode; label: string }[] = [];
	if (company.legalForm) {
		pills.push({ icon: <Building2 className="h-3.5 w-3.5" />, label: company.legalForm });
	}
	if (foundingYear) {
		pills.push({ icon: <Calendar className="h-3.5 w-3.5" />, label: `Zal. ${foundingYear}` });
	}
	if (company.employeeLabel) {
		pills.push({ icon: <Users className="h-3.5 w-3.5" />, label: company.employeeLabel });
	}
	if (company.naceDescription) {
		pills.push({ icon: <Factory className="h-3.5 w-3.5" />, label: company.naceDescription });
	}

	return (
		<div className="flex flex-col gap-4">
			{/* Avatar + name + status + watch */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex items-center gap-4 min-w-0">
					<div
						aria-hidden="true"
						className="flex h-13 w-13 shrink-0 items-center justify-center rounded-2xl bg-gradient-brand text-xl font-bold text-white shadow-glow"
					>
						{company.name.charAt(0).toUpperCase()}
					</div>
					<div className="flex flex-col gap-0.5 min-w-0">
						<p className="label-overline">Profil společnosti</p>
						<div className="flex items-center gap-3 min-w-0">
							<h1 className="text-2xl sm:text-3xl font-bold truncate">{company.name}</h1>
							<span className={`${status.className} shrink-0`}>{status.label}</span>
						</div>
					</div>
				</div>
				<ClientOnly>
					<WatchButton ico={company.ico} />
				</ClientOnly>
			</div>

			{/* Info pills */}
			{pills.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{pills.map((pill) => (
						<span
							key={pill.label}
							className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 ring-1 ring-gray-100"
						>
							{pill.icon}
							{pill.label}
						</span>
					))}
				</div>
			)}

			{/* Identity strip */}
			<div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground border-t border-gray-100 pt-3">
				<IdentityItem label="IČO" value={company.ico} copyLabel="Kopírovat IČO" />
				{dic && (
					<IdentityItem label="DIČ" value={dic} copyLabel="Kopírovat DIČ">
						{vat?.isUnreliable ? (
							<span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-600">
								<TriangleAlert className="h-3.5 w-3.5" />
								nespolehlivý plátce
							</span>
						) : (
							vat?.isVatPayer && (
								<span className="inline-flex items-center gap-0.5 text-xs font-medium text-green-600">
									<BadgeCheck className="h-3.5 w-3.5" />
									plátce DPH
								</span>
							)
						)}
					</IdentityItem>
				)}
				{company.dataBoxId && (
					<IdentityItem
						label="DS"
						value={company.dataBoxId}
						copyLabel="Kopírovat ID datové schránky"
						title="Datová schránka"
					/>
				)}
				{company.courtFileNumber && (
					<IdentityItem
						label="Sp. zn."
						value={company.courtFileNumber}
						copyLabel="Kopírovat spisovou značku"
						title={
							company.registrationCourt
								? `Spisová značka — ${company.registrationCourt}`
								: "Spisová značka"
						}
					/>
				)}
				{address && (
					<span className="flex items-center gap-1">
						<MapPin className="h-3 w-3" />
						{address}
						<ClientOnly>
							<CopyButton value={address} label="Kopírovat adresu sídla" />
						</ClientOnly>
					</span>
				)}
			</div>
		</div>
	);
}

function IdentityItem({
	label,
	value,
	copyLabel,
	title,
	children,
}: {
	label: string;
	value: string;
	copyLabel: string;
	title?: string;
	children?: React.ReactNode;
}) {
	return (
		<span className="inline-flex items-center gap-1" title={title}>
			<span className="font-mono">
				{label}: {value}
			</span>
			{children}
			<ClientOnly>
				<CopyButton value={value} label={copyLabel} />
			</ClientOnly>
		</span>
	);
}
